/**
 * Magic Link Login Page
 *
 * Landing page for magic link URLs (/magic-link?token=xxx).
 * Automatically verifies the token on mount via the VerifyMagicLink GraphQL mutation.
 * On success, stores JWT tokens and redirects to the home page (or stored redirect).
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { verifyMagicLink } from '../services/graphql/authService';
import '../styles/shared.css';

const MagicLinkPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing magic link token.');
      setLoading(false);
      return;
    }

    const verify = async () => {
      try {
        const { user, error: verifyError } = await verifyMagicLink(token);

        if (verifyError || !user) {
          setError(verifyError?.message || 'Magic link verification failed.');
          setLoading(false);
          return;
        }

        // Redirect to home or stored redirect after login
        const redirect = sessionStorage.getItem('postLoginRedirect') || '/';
        sessionStorage.removeItem('postLoginRedirect');
        navigate(redirect, { replace: true });
      } catch (err: any) {
        setError(err.message || 'An error occurred.');
        setLoading(false);
      }
    };

    verify();
  }, [token, navigate]);

  return (
    <main className="min-h-screen flex items-center justify-center bg-background text-foreground">
      <section className="w-full max-w-md mx-auto p-6 bg-card rounded-lg shadow-lg text-center">
        {loading ? (
          <>
            <h1 className="text-2xl font-bold mb-4">Logging you in...</h1>
            <p className="text-muted-foreground">Please wait while we verify your magic link.</p>
          </>
        ) : error ? (
          <>
            <h1 className="text-2xl font-bold mb-4">Login Failed</h1>
            <p className="text-destructive mb-4">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90"
            >
              Go to Login
            </button>
          </>
        ) : null}
      </section>
    </main>
  );
};

export default MagicLinkPage;
